import { listHsaPoolEntries } from "@redotech/redo-conversion-hsa-common/hsa-question-pool";
import { HsaQuestionType } from "@redotech/redo-conversion-hsa-common/hsa-question-type";
import {
  HsaQuestionGroupStep,
  hsaQuestionGroupStepSchema,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { useTranslation } from "@redotech/redo-i18n";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { remove, swap } from "@redotech/util/array";
import { memo, useMemo } from "react";
import {
  FrontendStepType,
  PartialFlowStep,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "..";
import { hsaQuestionTypeLabels } from "../../nodes/hsa-question-type-labels";
import { HsaTextField } from "./hsa-inspector-inputs";
import { HsaInspectorShell } from "./hsa-inspector-shell";
import { hsaOptionAnnotation } from "./hsa-question-display";

type GroupStep = PartialWithRequiredType<HsaQuestionGroupStep>;
type HydratedQuestion = NonNullable<GroupStep["questions"]>[number];

/** Handles referenced by any group in the flow — each pool question may only
 * be used once per flow. */
function usedQuestionHandles(steps: readonly PartialFlowStep[]): Set<string> {
  const used = new Set<string>();
  for (const step of steps) {
    if (step.type !== StepType.HSA_QUESTION_GROUP) {
      continue;
    }
    for (const entry of step.questions ?? []) {
      if (entry?.handle) {
        used.add(entry.handle);
      }
    }
  }
  return used;
}

const QuestionDetails = memo(function QuestionDetails({
  entry,
  controls,
}: {
  entry: HydratedQuestion;
  controls?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const question = entry?.question;
  const options =
    question && "options" in question ? (question.options ?? []) : [];
  const validationMessage =
    question && "validationMessage" in question
      ? question.validationMessage
      : undefined;

  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderRadius="lg"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="xs"
      px="xl"
      py="lg"
      w="full"
    >
      <Flex align="center" justify="space-between">
        <Text fontSize="sm" fontWeight="medium">
          {question?.label ?? entry?.handle}
        </Text>
        {controls}
      </Flex>
      <Flex gap="xs" wrap="wrap">
        {question?.type && (
          <RedoBadge
            color="gray"
            size="xs"
            text={hsaQuestionTypeLabels()[question.type]}
          />
        )}
        <RedoBadge
          color={question?.required ? "warning" : "gray"}
          size="xs"
          text={question?.required ? t("Required") : t("Optional")}
        />
      </Flex>
      {question?.description && (
        <Text fontSize="xs" textColor="secondary">
          {question.description}
        </Text>
      )}
      {options.length > 0 && (
        <Flex dir="column" gap="xxs">
          <Text fontSize="xxs" textColor="secondary">
            {t("Options")}
          </Text>
          {options.map((option, index) => {
            const annotation = hsaOptionAnnotation(question, option?.value);
            return (
              <Flex align="center" gap="xs" key={option?.value ?? index}>
                <Text fontSize="xs" textColor="secondary">
                  •
                </Text>
                <Text fontSize="xs">{option?.label ?? option?.value}</Text>
                {annotation && (
                  <RedoBadge
                    color="gray"
                    size="xs"
                    text={
                      annotation === "Write-in" ? t("Write-in") : t("Exclusive")
                    }
                  />
                )}
              </Flex>
            );
          })}
        </Flex>
      )}
      {question?.type === HsaQuestionType.DYNAMIC_MULTI_SELECT_QUESTION && (
        <Text fontSize="xs" textColor="secondary">
          {t("Options are provided at checkout from the shopper's cart.")}
        </Text>
      )}
      {validationMessage && (
        <Text fontSize="xs" textColor="secondary">
          {t("Validation: {{validationMessage}}", { validationMessage })}
        </Text>
      )}
    </Flex>
  );
});

const AddQuestionPicker = memo(function AddQuestionPicker({
  steps,
  onAdd,
}: {
  steps: readonly PartialFlowStep[];
  onAdd: (entry: HydratedQuestion) => void;
}) {
  const { t } = useTranslation();
  const options = useMemo(() => {
    const used = usedQuestionHandles(steps);
    return listHsaPoolEntries()
      .filter((entry) => !used.has(entry.handle))
      .map(
        (entry): RedoListItem<HydratedQuestion> => ({
          id: entry.handle,
          type: "text",
          text: entry.question.label,
          supportingText: entry.handle,
          value: { handle: entry.handle, question: entry.question },
        }),
      );
  }, [steps]);

  return (
    <RedoSingleSelectDropdownInput<HydratedQuestion>
      disabled={options.length === 0}
      fitToAnchor={false}
      options={options}
      optionSelected={({ item }) => onAdd(item.value)}
      placeholder={
        options.length
          ? t("Add a question from the pool")
          : t("Every pool question is already used in this flow")
      }
      selectedItem={undefined}
      size="xs"
    />
  );
});

const HsaQuestionGroupInspector = memo(function HsaQuestionGroupInspector(
  props: StepTypeDetailsProps<GroupStep>,
) {
  const { t } = useTranslation();
  const { step, steps, setStep, onClose, readonly } = props;
  const questions = step.questions ?? [];
  return (
    <HsaInspectorShell
      onClose={onClose}
      subtitle={step.groupHandle}
      title={step.title || t("Question group")}
    >
      <Text fontSize="xs" textColor="secondary">
        {t("Shoppers answer these questions one at a time, in this order.")}
      </Text>
      {!readonly && (
        <>
          <HsaTextField
            description={t(
              "Stable kebab-case identifier, unique within the flow.",
            )}
            label={t("Handle")}
            onCommit={(groupHandle) => setStep({ ...step, groupHandle })}
            placeholder="medical-info"
            value={step.groupHandle}
          />
          <HsaTextField
            description={t("Optional, for analytics and admin tooling.")}
            label={t("Title")}
            onCommit={(title) =>
              setStep({ ...step, title: title || undefined })
            }
            placeholder={t("Medical information")}
            value={step.title}
          />
        </>
      )}
      <Flex dir="column" gap="md">
        {questions.length ? (
          questions.map((entry, index) => (
            <QuestionDetails
              controls={
                readonly ? undefined : (
                  <Flex gap="xxs">
                    <RedoButton
                      aria-label={t("Move question up")}
                      disabled={index === 0}
                      hierarchy="tertiary"
                      IconLeading={ChevronUpIcon}
                      onClick={() =>
                        setStep({
                          ...step,
                          questions: swap([...questions], index - 1, index),
                        })
                      }
                      size="xs"
                    />
                    <RedoButton
                      aria-label={t("Move question down")}
                      disabled={index === questions.length - 1}
                      hierarchy="tertiary"
                      IconLeading={ChevronDownIcon}
                      onClick={() =>
                        setStep({
                          ...step,
                          questions: swap([...questions], index, index + 1),
                        })
                      }
                      size="xs"
                    />
                    <RedoButton
                      aria-label={t("Remove question")}
                      hierarchy="tertiary"
                      IconLeading={TrashIcon}
                      onClick={() =>
                        setStep({
                          ...step,
                          questions: remove(questions, index),
                        })
                      }
                      size="xs"
                    />
                  </Flex>
                )
              }
              entry={entry}
              key={entry?.handle ?? index}
            />
          ))
        ) : (
          <Text fontSize="sm" textColor="secondary">
            {t("No questions in this group.")}
          </Text>
        )}
      </Flex>
      {!readonly && (
        <AddQuestionPicker
          onAdd={(entry) =>
            setStep({ ...step, questions: [...questions, entry] })
          }
          steps={steps}
        />
      )}
    </HsaInspectorShell>
  );
});

export const hsaQuestionGroupStepType: FrontendStepType<GroupStep> = {
  type: "custom",
  // The persisted schema requires at least one question handle per group.
  valid: (step) =>
    hsaQuestionGroupStepSchema.safeParse(step).success &&
    (step.questions?.length ?? 0) >= 1,
  customInspector: HsaQuestionGroupInspector,
};
